import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

const CATS = ["All","SOP","Policy","EPAR","EW","IRW","Reference"];
export function KnowledgeBase() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [openArt, setOpenArt] = useState(null);
  useEffect(() => {
    axios.get(`${API}/kb`, { params: { q: q || undefined, category: cat==="All"?undefined:cat } }).then(r=>setItems(r.data));
  }, [q, cat]);
  useEffect(() => {
    if (!openId) return;
    let cancelled = false;
    axios.get(`${API}/kb/${openId}`).then(r => { if (!cancelled) setOpenArt(r.data); });
    return () => { cancelled = true; };
  }, [openId]);
  const closeModal = () => { setOpenId(null); setOpenArt(null); };
  return (
    <div className="fade-up" data-testid="kb-page">
      <div className="label-upper mb-1">Reference</div>
      <h1 className="font-heading text-3xl font-bold mb-1">Knowledge Base</h1>
      <p className="text-sm text-slate-500 mb-6">Searchable DRFA guidelines, fact sheets and SOPs the assessor team relies on.</p>

      <div className="card p-4 mb-5 flex items-center gap-3">
        <Search className="w-4 h-4 text-slate-400"/>
        <input className="input border-0 focus:!ring-0 !p-0" placeholder="Search articles…" value={q} onChange={e=>setQ(e.target.value)} data-testid="kb-search-input" />
        <div className="flex gap-1 ml-auto">
          {CATS.map(c => (
            <button key={c} onClick={()=>setCat(c)} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${cat===c?"bg-[#002664] text-white":"bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid={`kb-cat-${c.toLowerCase()}`}>{c}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map(a => (
          <button key={a.id} onClick={()=>setOpenId(a.id)} className="card card-hover p-5 text-left" data-testid={`kb-card-${a.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500">{a.category}</div>
              {a.applies_to && a.applies_to.length <= 2 && (
                <div className="text-[9.5px] tracking-[0.12em] uppercase text-slate-400">· {a.applies_to.join(", ").replace(/Approval/g,"").replace(/Finalisation/g,"")}</div>
              )}
            </div>
            <div className="font-heading font-semibold text-[15px] leading-snug mb-1">{a.title}</div>
            <div className="text-[12.5px] text-slate-500 leading-snug">{a.summary}</div>
            <div className="mt-3 flex items-center gap-3 text-[11px]">
              <span className="text-blue-600 font-semibold">Read summary →</span>
              {a.url && <span className="text-slate-400">·</span>}
              {a.url && <a href={a.url} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1" data-testid={`kb-source-${a.id}`}><LinkIcon className="w-3 h-3"/> Open source PDF</a>}
            </div>
          </button>
        ))}
        {items.length === 0 && <div className="text-sm text-slate-500">No articles match.</div>}
      </div>

      {openArt && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={closeModal} data-testid="kb-modal">
          <div className="bg-white max-w-2xl w-full rounded-md border border-slate-200 p-7 max-h-[85vh] overflow-auto" onClick={e=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-1">{openArt.category}{openArt.applies_to ? ` · applies to ${openArt.applies_to.join(", ")}`:""}</div>
                <h2 className="font-heading text-2xl font-bold">{openArt.title}</h2>
              </div>
              <button onClick={closeModal} className="p-1 hover:bg-slate-100 rounded" data-testid="kb-close-button"><X className="w-4 h-4"/></button>
            </div>
            <div className="text-[13.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">{openArt.body}</div>
            {openArt.url && (
              <a href={openArt.url} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2 mt-5" data-testid="kb-modal-source-link"><LinkIcon className="w-4 h-4"/> Open authoritative source</a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

